import React, { useState, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import FlowBuilder from './pages/FlowBuilder';
import Header from './components/Header';
import Footer from './components/Footer';
import { FlowProvider, useFlowContext } from './context/FlowContext';

import IntentsPage from './pages/IntentsPage';

function App() {
  return (
    <FlowProvider>
      <AppContent />
    </FlowProvider>
  );
}

function AppContent() {
  const { flowName, setFlowName, notification, hideNotification } = useFlowContext();
  const [isIntentManagerOpen, setIsIntentManagerOpen] = useState(false);
  const [startNode, setStartNode] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loadFlowFunction, setLoadFlowFunction] = useState(null);
  const flowBuilderRef = useRef(null);
  const navigate = useNavigate();

  const handleSaveFlow = (saveData) => {
    if (flowBuilderRef.current && flowBuilderRef.current.saveFlow) {
      flowBuilderRef.current.saveFlow(saveData);
    }
  };

  const handleAddCondition = () => {
    setIsIntentManagerOpen(true);
  };

  // Handler to receive loadFlow function from FlowBuilder
  const handleSetLoadFlowFunction = useCallback((fn) => {
    console.log('Setting load flow function:', fn);
    setLoadFlowFunction(() => fn);
  }, []);

  // Handler to pass to Header for loading flows
  const handleLoadFlow = useCallback((flow) => {
    console.log('Attempting to load flow:', flow);
    if (loadFlowFunction) {
      loadFlowFunction(flow);
    } else {
      // If we're not in the builder yet, navigate there first
      navigate('/builder');
      // Store the flow to load after navigation
      sessionStorage.setItem('pendingFlow', JSON.stringify(flow));
    }
  }, [loadFlowFunction, navigate]);

  // Effect to handle pending flow load after navigation
  React.useEffect(() => {
    const pendingFlow = sessionStorage.getItem('pendingFlow');
    if (pendingFlow && loadFlowFunction) {
      const flow = JSON.parse(pendingFlow);
      loadFlowFunction(flow);
      sessionStorage.removeItem('pendingFlow');
    }
  }, [loadFlowFunction]);

  return (
    <div className="app">
      {/* Global Notification */}
      {notification.show && (
        <div
          className={`global-notification ${notification.type}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336',
            border: 'none',
            fontSize: '14px',
            maxWidth: '400px',
            wordWrap: 'break-word'
          }}
          onClick={hideNotification}
        >
          {notification.message}
        </div>
      )}

      <Header
        flowName={flowName}
        onFlowNameChange={setFlowName}
        onSaveFlow={handleSaveFlow}
        onLoadFlow={handleLoadFlow}
        onAddCondition={handleAddCondition}
        startNode={startNode}
        setStartNode={setStartNode}
        nodes={nodes}
      />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/intents" element={<IntentsPage />} />
          <Route
            path="/builder"
            element={
              <FlowBuilder
                isIntentManagerOpen={isIntentManagerOpen}
                setIsIntentManagerOpen={setIsIntentManagerOpen}
                ref={flowBuilderRef}
                startNode={startNode}
                setStartNode={setStartNode}
                nodes={nodes}
                setNodes={setNodes}
                onSetLoadFlow={handleSetLoadFlowFunction}
              />
            }
          />
          <Route
            path="/builder/:flowId"
            element={
              <FlowBuilder
                isIntentManagerOpen={isIntentManagerOpen}
                setIsIntentManagerOpen={setIsIntentManagerOpen}
                ref={flowBuilderRef}
                startNode={startNode}
                setStartNode={setStartNode}
                nodes={nodes}
                setNodes={setNodes}
                onSetLoadFlow={handleSetLoadFlowFunction}
              />
            }
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;


